<template>
  <div class="overflow-y-auto p-4">
    <el-card
      class="rounded-lg border bg-card text-card-foreground shadow-2xs mb-6"
    >
      <div class="flex flex-col space-y-1.5 p-6">
        <h3 class="text-2xl font-semibold leading-none tracking-tight">
          Filters &amp; Search
        </h3>
      </div>
      <el-form label-position="top">
        <div class="p-6 pt-0">
          <div class="grid gap-4 md:grid-cols-7">
            <div class="md:col-span-2">
              <div class="flex flex-col gap-2">
                <label
                  class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  Search
                </label>
                <DebounceInput
                  placeholder="Search events by ID or description"
                  v-model="tableCtrl.queryParameters.search"
                  clearable
                >
                  <template #prepend>
                    <i class="el-icon-search"></i>
                  </template>
                </DebounceInput>
              </div>
            </div>
            <div class="flex flex-col gap-2">
              <label
                class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Device
              </label>
              <SmartSelect
                v-model="tableCtrl.queryParameters.deviceKey"
                :add-all-option="{ key: undefined, value: 'All Devices' }"
                :load-method="getAllAssetVo"
                value-field="deviceKey"
                display-field="name"
              />
            </div>
            <div class="flex flex-col gap-2">
              <label
                class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Level
              </label>
              <SmartSelect
                v-model="tableCtrl.queryParameters.level"
                :options="enumToOptions(EventLogLevel)"
                class="select-width"
                placeholder="Level"
                valueType="number"
                valueField="id"
                clearable
              />
            </div>
            <div class="flex flex-col gap-2">
              <label
                class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Status
              </label>
              <SmartSelect
                v-model="tableCtrl.queryParameters.status"
                :options="enumToOptions(EventLogStatus)"
                class="select-width"
                placeholder="Status"
                valueType="number"
                valueField="id"
                clearable
              />
            </div>
            <div class="flex flex-col gap-2 col-span-2">
              <label
                class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Date Range
              </label>
              <DatePickerStartEnd
                :startDate.sync="tableCtrl.queryParameters.startTime"
                :endDate.sync="tableCtrl.queryParameters.endTime"
                start-placeholder="Start date"
                end-placeholder="End date"
                range-separator="To"
              />
            </div>
          </div>
        </div>
      </el-form>
    </el-card>
    <el-card class="rounded-lg border bg-card text-card-foreground shadow-2xs">
      <div class="flex flex-row justify-between items-center py-4">
        <div></div>
        <div class="flex flex-row"></div>
      </div>
      <div class="p-0">
        <div class="relative w-full overflow-auto">
          <el-table
            ref="tableRef"
            v-loading="tableCtrl.state.loading"
            element-loading-background="rgba(24, 33, 48, 0.8)"
            :data="tableCtrl.state.tableData"
            style="width: 100%"
            @selection-change="handleSelectionChange"
          >
            <el-table-column type="selection" width="55"> </el-table-column>
            <el-table-column label="Event ID" prop="event.id" min-width="80">
              <template slot-scope="scope">
                &#35; {{ scope.row.id ?? "" }}
              </template>
            </el-table-column>
            <el-table-column
              label="Description"
              prop="event.event"
              min-width="200"
            >
              <template slot-scope="scope">
                <div style="word-break: break-word">
                  {{ scope.row.event ?? "" }}
                </div>
              </template>
            </el-table-column>
            <el-table-column
              label="Device"
              prop="event.assetName"
              min-width="120"
              align="center"
            >
              <template slot-scope="scope">
                {{ scope.row.assetName ?? "" }}
              </template>
            </el-table-column>
            <el-table-column label="Level" prop="event.level" min-width="80">
              <template slot-scope="scope">
                {{ enumValueToLabel(EventLogLevel, scope.row.level) }}
              </template>
            </el-table-column>
            <el-table-column label="Status" prop="event.status" min-width="80">
              <template slot-scope="scope">
                {{ enumValueToLabel(EventLogStatus, scope.row.status) }}
              </template>
            </el-table-column>
            <el-table-column
              label="Timestamp"
              prop="event.timestamp"
              min-width="160"
            >
              <template slot-scope="scope">
                {{
                  dayjs(scope.row.timestamp).format("YYYY-MM-DD HH:mm:ss") ?? ""
                }}
              </template>
            </el-table-column>

            <el-table-column
              label="Actions"
              width="180"
              align="right"
              fixed="right"
            >
              <template slot-scope="scope">
                <ActionButtons
                  :actions="actions"
                  :item="scope.row"
                  @command="handleCommand"
                />
              </template>
            </el-table-column>
          </el-table>
          <div class="flex mt-4 justify-end">
            <el-pagination
              @size-change="(val) => tableCtrl.setPageSize(val)"
              @current-change="(val) => tableCtrl.setCurrentPage(val)"
              :current-page="tableCtrl.state.pagination.page"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="tableCtrl.state.pagination.pageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableCtrl.state.pagination.total"
            />
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import SmartSelect from "@/components/SmartControls/SmartSelect.vue";
import ActionButtons from "@/components/ActionButtons.vue";
import { TableController } from "@/controllers/TableController";
import BotSvg from "!!raw-loader!@/assets/bot.svg";
import BrainSvg from "!!raw-loader!@/assets/brain.svg";
import { getEvents, deleteAiAuditById, getAgents, getAllAssetVo } from "@/api";
import {
  EventLogLevel,
  EventLogStatus,
  enumValueToLabel,
  enumToOptions,
} from "@/enums";
import dayjs from "dayjs";

export default {
  name: "AiAuditEventList",
  components: {
    SmartSelect,
    ActionButtons,
  },
  emits: ["tabIndex"],
  data() {
    return {
      BotSvg,
      BrainSvg,
      tabIndex: 0,
      tableCtrl: new TableController({
        loadMethod: this.fetchData,
        autoLoad: true,
        isClientPaged: false,
        pageSize: 10,
        keyField: "id",
        pageField: "offset",
        pageSizeField: "limit",
        queryParameters: {
          level: undefined,
          status: undefined,
          type: undefined,
          search: undefined,
          startDate: null,
          endDate: null,
          deviceKey: undefined,
        },
      }),
    };
  },
  computed: {
    EventLogLevel: () => EventLogLevel,
    EventLogStatus: () => EventLogStatus,
    actions: () => [
      {
        type: "button",
        label: "Feedback",
        command: "feedback",
        icon: "el-icon-chat-line-square",
      },
    ],
  },

  watch: {
    "tableCtrl.queryParameters": {
      handler() {
        this.tableCtrl.setCurrentPage(1);
        this.handleRefresh();
      },
      deep: true,
    },
  },
  methods: {
    enumToOptions,
    enumValueToLabel,
    getAgents,
    getAllAssetVo,
    dayjs,
    async handleRefresh() {
      this.tableCtrl.setCurrentPage(1);
      await this.tableCtrl.refresh();
    },
    async fetchData(params) {
      const res = await getEvents(params);
      return res;
    },
    updateValue(value) {
      this.emit("input", value);
    },
    async handleAdd() {
      const me = this;
      await me.openEditDialog(null, "edit");
      me.$message.success("Audit created successfully");
      await me.handleRefresh();
    },
    async handleCommand({ command, item }) {
      const me = this;
      if (["view", "edit", "feedback"].includes(command)) {
        await me.openEditDialog(item, command);
      } else if (command === "ai-learning") {
        await me.openChatDialog(item);
      } else if (command === "delete") {
        await me.handleDelete(item);
      }
      me.$message.success("Operation successful");
      await me.handleRefresh();
    },
    async handleDelete(item) {
      const me = this;
      await me.$confirm(
        `Are you sure you want to delete ${item?.audit?.feedback}?`,
        "Warning",
        {
          type: "warning",
        }
      );
      await deleteAiAuditById(item?.audit?.id);
      me.$message.success("Delete successful");
    },
    async openEditDialog(item, command) {
      await this.$loaderWindow({
        title: `${command.capitalize()} Event Detail`,
        component: () => import("./EventDetailDialog.vue"),
        props: {
          value: item,
        },
      });
    },
    handleChangeTab(index) {
      this.$emit("tabIndex", index);
    },
    handleSelectionChange(selection) {
      this.tableCtrl.handleSelectionChange(selection);
    },
    async handleLearning() {
      if (this.tableCtrl.selectedCount === 0) {
        this.$message.warning(
          "Please select at least one event for AI learning."
        );
        return;
      }
    },
    async handleBatchDelete() {
      if (this.tableCtrl.selectedCount === 0) {
        this.$message.warning("Please select at least one event to delete.");
        return;
      }
      await this.$confirm(
        `Are you sure you want to delete ${this.tableCtrl.selectedCount} selected events?`,
        "Warning",
        {
          type: "warning",
        }
      );
    },
    async openChatDialog(item) {
      const aiAgent = JSON.parse(item?.agentInfo);
      await this.$loaderWindow({
        title: `Chat with ${aiAgent?.agentId}`,
        component: () => import("@/pages/ai-agent/components/ChatDialog.vue"),
        height: "80%",
        width: "70%",
        showHeader: false,
        padding: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
        props: {
          agent: {
            agent_id: aiAgent?.agentId,
            conversationId: aiAgent?.conversationId,
          },
        },
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.tableCtrl.tableRef = this.$refs.tableRef;
    });
  },
};
</script>
